import { cloneDeep } from 'lodash-es';

import { ICartEntry } from '@rbi-ctg/menu';
import { CartEntryType } from 'utils/cart';

export const mapEntryTypeToOfferType = (entryType: CartEntryType) => {
  let offerType;

  switch (entryType) {
    case CartEntryType.combo:
    case CartEntryType.offerCombo:
      offerType = CartEntryType.offerCombo;
      break;
    case CartEntryType.item:
    case CartEntryType.offerItem:
    default:
      offerType = CartEntryType.offerItem;
      break;
  }

  return offerType;
};

/**
 * Recursively find a match for the swapId on the cartEntry or its children.
 *
 * @param cartEntry
 * @param swapId
 */
const deepFindCartEntryForSwap = (
  cartEntry: ICartEntry,
  swapId: string
): ICartEntry | undefined => {
  if (cartEntry._id === swapId) {
    return cartEntry;
  }

  let match: ICartEntry | undefined;
  for (const child of cartEntry.children) {
    match = deepFindCartEntryForSwap(child, swapId);
    if (match) {
      break;
    }
  }

  return match;
};

const excludedCartTypes = new Set([
  CartEntryType.offerCombo,
  CartEntryType.offerItem,
  CartEntryType.offerDiscount,
]);

type IEntriesForSwap = {
  rootEntry: ICartEntry | null;
  childEntry: ICartEntry | null;
};

/**
 * Return the first cartEntry that matches the swapId.
 * If the match was found within the children of the cartEntry, it returns that reference as well.
 *
 * @param cartEntries
 * @param swapId
 */
export const findEntriesForSwap = (cartEntries: ICartEntry[], swapId: string): IEntriesForSwap => {
  let entries: IEntriesForSwap = { rootEntry: null, childEntry: null };

  for (const rootEntry of cartEntries) {
    // Swaps can't be applied to OfferCombo / OfferItem
    if (excludedCartTypes.has(rootEntry.type)) {
      continue;
    }

    const childEntry = deepFindCartEntryForSwap(rootEntry, swapId);
    if (childEntry) {
      entries = { rootEntry, childEntry };
      break;
    }
  }

  return entries;
};

/**
 * Return the ids of cartEntries applicable for swaps.
 *
 * @param cartEntries
 */
export const getCartEntriesIdsForSwap = (cartEntries: ICartEntry[]): Set<string> => {
  const cartEntriesIds = new Set<string>();

  // Recursive loop
  const addCartEntriesIdsToSet = (currentEntries: ICartEntry[], currentSet: Set<string>) => {
    for (const cartEntry of currentEntries) {
      // Swaps can't be applied to OfferCombo / OfferItem
      if (excludedCartTypes.has(cartEntry.type)) {
        continue;
      }

      if (cartEntry.children?.length) {
        addCartEntriesIdsToSet(cartEntry.children, currentSet);
      }

      currentSet.add(cartEntry.sanityId || cartEntry._id);
    }
  };

  addCartEntriesIdsToSet(cartEntries, cartEntriesIds);
  return cartEntriesIds;
};

/**
 * Traverses cartEntries and replaces the matching item with newEntry
 *
 * @param cartEntries
 * @param swapId
 * @param newEntry
 */
export const replaceEntryForSwap = (
  originalCartEntries: ICartEntry[],
  swapId: string,
  newEntry: ICartEntry
) => {
  // Avoid mutation on the arguments
  const cartEntries = cloneDeep(originalCartEntries);

  const iterator = (entry: ICartEntry, index: number, array: ICartEntry[]) => {
    // Swaps can't be applied to OfferCombo / OfferItem
    if (excludedCartTypes.has(entry.type)) {
      return false;
    }
    if (entry._id === swapId) {
      array[index] = newEntry;
      return true;
    }
    return entry.children.length && entry.children.some(iterator);
  };

  cartEntries.some(iterator);

  return [...cartEntries];
};
